import React, { useState, useRef } from 'react'
import { chatService } from '../lib/chat-service'
// For Lovable (current)
import { supabase } from '@/integrations/supabase/client'
// For local development with env variables (commented out)
// import { supabase } from '../lib/supabase'
import { Paperclip, Bot, Send, X, FileText, Image, Video, Music, File } from 'lucide-react'
import { useRateLimiting } from '@/hooks/use-rate-limiting';
import { toast } from '@/hooks/use-toast';

interface MessageSenderProps {
  channelId: string
  currentUser: any
  onMessageSent?: (message: any) => void
  channelName?: string
}

export default function MessageSender({ channelId, currentUser, onMessageSent, channelName }: MessageSenderProps) {
  const [message, setMessage] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const [filePreviews, setFilePreviews] = useState<{[key: string]: string}>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Rate limiting for chat messages
  const { checkRateLimit, recordAction } = useRateLimiting({
    action: 'chat:message',
    identifier: channelId,
    onRateLimitExceeded: (result) => {
      const retryAfter = result.retryAfter || 0;
      const seconds = Math.ceil(retryAfter);
      toast({
        title: "Message Rate Limit Exceeded",
        description: `Please wait ${seconds} seconds before sending another message.`,
        variant: "destructive",
      });
    }
  });

  // Rate limiting for file uploads
  const { checkRateLimit: checkFileUploadLimit, recordAction: recordFileUpload } = useRateLimiting({
    action: 'file:upload',
    identifier: channelId,
    onRateLimitExceeded: (result) => {
      const retryAfter = result.retryAfter || 0;
      const seconds = Math.ceil(retryAfter);
      toast({
        title: "File Upload Rate Limit Exceeded",
        description: `Please wait ${seconds} seconds before uploading another file.`,
        variant: "destructive",
      });
    }
  });

  // Helper function to get current workspace ID
  const getCurrentWorkspaceId = async (): Promise<string | null> => {
    try {
      // In development, check URL parameter first
      if (import.meta.env.DEV) {
        const urlParams = new URLSearchParams(window.location.search);
        const tenantSlug = urlParams.get('tenant');
        if (tenantSlug && tenantSlug !== 'default') {
          const { data: workspace } = await (supabase as any)
            .from('watt_workspaces')
            .select('id')
            .eq('slug', tenantSlug)
            .single();
          return workspace?.id || null;
        }
      }

      // Get user's current/default workspace
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: membership } = await (supabase as any)
        .from('workspace_memberships')
        .select('workspace_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('is_default', { ascending: false })
        .limit(1)
        .single();

      return membership?.workspace_id || null;
    } catch (error) {
      console.error('Error getting current workspace ID:', error);
      return null;
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setSelectedFiles(files)
    
    // Generate previews for image files
    const previews: {[key: string]: string} = {}
    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          previews[`${index}-${file.name}`] = e.target?.result as string
          setFilePreviews(prev => ({ ...prev, ...previews }))
        }
        reader.readAsDataURL(file)
      }
    })
  }

  const handleRemoveFile = (fileToRemove: File) => {
    const index = selectedFiles.indexOf(fileToRemove)
    setSelectedFiles(prev => prev.filter(file => file !== fileToRemove))
    // Remove preview for this file
    const previewKey = `${index}-${fileToRemove.name}`
    setFilePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[previewKey]
      return newPreviews
    })
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image className="w-4 h-4" />
    if (fileType.startsWith('video/')) return <Video className="w-4 h-4" />
    if (fileType.startsWith('audio/')) return <Music className="w-4 h-4" />
    if (fileType.includes('pdf')) return <FileText className="w-4 h-4" />
    return <File className="w-4 h-4" />
  }

  const handleKeyPress = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      await handleSendMessage()
    }
  }

  const handleSendMessage = async () => {
    if (!message.trim() && selectedFiles.length === 0) return;

    // Check rate limit before sending message
    const rateLimitResult = await checkRateLimit();
    if (!rateLimitResult.allowed) {
      return;
    }

    setSending(true);

    try {
      // Get workspace ID for file uploads
      const workspaceId = await getCurrentWorkspaceId();
      if (!workspaceId) {
        toast({
          title: "Error",
          description: "Could not determine workspace. Please refresh the page.",
          variant: "destructive",
        });
        return;
      }

      // Handle file uploads first
      const uploadedFiles: any[] = [];
      if (selectedFiles.length > 0) {
        // Check file upload rate limit
        const fileRateLimitResult = await checkFileUploadLimit();
        if (!fileRateLimitResult.allowed) {
          setSending(false);
          return;
        }

        for (const file of selectedFiles) {
          try {
            const sanitizedFilename = sanitizeFilename(file.name);
            const { data, error } = await supabase.storage
              .from('watt-attachments')
              .upload(`${workspaceId}/${channelId}/${currentUser.id}/${Date.now()}-${sanitizedFilename}`, file);

            if (error) {
              console.error('Upload error:', error);
              toast({
                title: "Upload Error",
                description: `Failed to upload ${file.name}: ${error.message}`,
                variant: "destructive",
              });
              continue;
            }

            uploadedFiles.push({
              name: file.name,
              size: file.size,
              type: file.type,
              path: data.path
            });

            // Record file upload
            await recordFileUpload();
          } catch (error) {
            console.error('Error processing AI message:', error);
            toast({
              title: "Upload Error",
              description: `Failed to upload ${file.name}`,
              variant: "destructive",
            });
          }
        }
      }

      // Send message with attachments
      if (message.trim() || uploadedFiles.length > 0) {
        
        const { data, error } = await chatService.sendMessage(
          channelId,
          message.trim(),
          uploadedFiles
        );
        
        if (error) {
          console.error('Error sending message:', error);
          toast({
            title: "Error",
            description: "Failed to send message. Please try again.",
            variant: "destructive",
          });
        } else {
          // Record successful message
          await recordAction();
          
          // Clear form
          setMessage('');
          setSelectedFiles([]);
          setFilePreviews({});
          
          // Notify parent component
          if (onMessageSent && data) {
            onMessageSent(data);
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const sanitizeFilename = (filename: string): string => {
    // Remove or replace problematic characters for S3 compatibility
    return filename
      .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace spaces and special chars with underscore
      .replace(/_{2,}/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, '') // Remove leading/trailing underscores
      .substring(0, 200); // Limit length to avoid S3 key length issues
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="p-4 border-t bg-background">
      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <div className="mb-3">
          <div className="text-sm text-muted-foreground mb-2">
            Selected files ({selectedFiles.length})
          </div>
          <div className="flex flex-wrap gap-3">
            {selectedFiles.map((file, index) => {
              const previewKey = `${index}-${file.name}`
              const isImage = file.type.startsWith('image/')
              const preview = filePreviews[previewKey]
              
              return (
                <div key={index} className="relative bg-muted rounded-lg p-3 max-w-xs">
                  {/* Remove button */}
                  <button
                    onClick={() => handleRemoveFile(file)}
                    className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-6 h-6 flex items-center justify-center hover:bg-destructive/90 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  
                  {/* File preview */}
                  <div className="mb-2">
                    {isImage && preview ? (
                      <img 
                        src={preview} 
                        alt={file.name}
                        className="w-full h-24 object-cover rounded"
                      />
                    ) : (
                      <div className="w-full h-24 bg-background rounded flex items-center justify-center">
                        {getFileIcon(file.type)}
                      </div>
                    )}
                  </div>
                  
                  {/* File info */}
                  <div className="text-sm">
                    <div className="font-medium truncate" title={file.name}>
                      {file.name}
                    </div>
                    <div className="text-muted-foreground">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Message Input Container */}
      <div className="flex items-center gap-3 p-2 border rounded-lg bg-background">
        {/* Hidden File Input */}
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        
        {/* Paperclip Icon */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!channelId || sending}
          className="p-2 text-muted-foreground hover:text-foreground disabled:text-muted-foreground/50 transition-colors"
          title="Attach files"
        >
          <Paperclip className="w-5 h-5" />
        </button>

        {/* Message Input */}
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={`Message ${channelName ? '#' + channelName : ''}`}
          className="flex-1 bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
          disabled={!channelId || sending}
        />
        
        {/* Bot Icon */}
        <button
          onClick={() => setMessage(prev => prev.startsWith('hey watt ') ? prev : 'hey watt ')}
          disabled={true}
          className="p-2 text-muted-foreground hover:text-foreground disabled:text-muted-foreground/50 transition-colors"
          title="Coming Soon"
        >
          <Bot className="w-5 h-5" />
        </button>
        
        {/* Send Button */}
        <button
          onClick={handleSendMessage}
          disabled={(!message.trim() && selectedFiles.length === 0) || !channelId || sending}
          className="p-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground transition-colors"
          title="Send message"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
} 